import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Package, Receipt } from "lucide-react";

export default function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/orders").then(r => setOrders(r.data || []));
  }, [user]);

  if (orders === null) return <div className="pt-32 text-center text-zinc-500" data-testid="orders-loading">Loading…</div>;

  return (
    <div data-testid="orders-page" className="pt-28 pb-24 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">Your purchases</p>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2 mb-10">Order history</h1>

      {orders.length === 0 ? (
        <div data-testid="orders-empty" className="card-surface p-12 text-center">
          <Receipt className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-400">No orders yet — your future treasures live here.</p>
          <Link to="/browse" className="btn-secondary inline-block mt-6">Browse the marketplace</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <Link key={o.id} to={`/orders/${o.id}`} data-testid={`order-row-${o.id}`} className="card-surface p-6 block hover:border-[#FF007F]/40">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Order</p>
                  <p className="font-mono text-xs text-zinc-300 mt-1">#{o.id.slice(0, 8).toUpperCase()}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Placed</p>
                  <p className="text-sm text-zinc-300 mt-1">{new Date(o.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Status</p>
                  <span className={`text-[10px] uppercase tracking-widest border px-2 py-1 inline-block mt-1 ${
                    o.status === "delivered" ? "text-[#CCFF00] border-[#CCFF00]/30"
                    : o.status === "shipped" ? "text-[#05D9E8] border-[#05D9E8]/30"
                    : "text-[#FFB800] border-[#FFB800]/30"
                  }`}>{o.status}</span>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Total</p>
                  <p className="font-display font-black text-2xl text-[#FF007F] mt-1">${Number(o.total).toFixed(2)}</p>
                </div>
              </div>
              <ul className="divide-y divide-white/5 border-t border-white/5 pt-2">
                {(o.items || []).map((it, i) => (
                  <li key={i} className="flex items-center justify-between py-3 text-sm">
                    <div className="flex items-center gap-3">
                      <Package className="w-4 h-4 text-zinc-500" />
                      <span>{it.title}</span>
                      <span className="text-zinc-500 text-xs">× {it.qty}</span>
                    </div>
                    <span className="text-zinc-400">${(Number(it.price) * Number(it.qty)).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              {o.tracking && (
                <p className="mt-4 text-xs text-zinc-400 flex items-center gap-2">
                  <span className="uppercase tracking-widest text-zinc-500">Tracking</span>
                  <span className="text-white font-bold">{o.tracking.carrier}</span>
                  <span className="font-mono">{o.tracking.number}</span>
                </p>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
